import classNames from 'classnames';
import { Fragment } from 'react';
import { withTheme } from '@mui/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import styled from '@emotion/styled';

import { breadcrumbPaths, IRoutePath } from '@selfserve/react-utils';
import { StyledContainer, StyledText } from '@selfserve/react-ui';
import { useCurrentRoute } from '@selfserve/react-hooks';

const StyledBreadcrumbContainer = styled(StyledContainer)`
  height: fit-content;
  margin-bottom: 40px;
  padding-left: 20px;
`;

const StyledBreadcrumbLine = styled.div(
  ({ theme, isBeforeReady }: { theme?: any; isBeforeReady?: boolean }) => ({
    margin: '0 16px',
    ...(isBeforeReady && {
      margin: '0 24px 0 16px',
    }),
    flex: 1,
    background: theme.palette.grey['500'],
    height: '4px',
    borderRadius: '4px 0 0 4px',
  })
);

const StyledBreadcrumbText = styled(StyledText)(
  ({
    theme,
    isActive,
    isFinished,
  }: {
    theme?: any;
    isActive?: boolean;
    isFinished?: boolean;
  }) => ({
    position: 'relative',
    color: isActive ? theme.palette.primary.main : theme.palette.grey['300'],
    '&:before': {
      content: '""',
      position: 'absolute',
      left: '-20px',
      top: '50%',
      ...(isActive && {
        width: '12px',
        height: '12px',
        background: theme.palette.primary.main,
        borderRadius: '50%',
        marginTop: '-6px',
      }),
      ...(isFinished && {
        width: '9px',
        height: '9px',
        background: 'no-repeat url("assets/union.svg") center',
        marginTop: '-4px',
      }),
    },
  })
);

interface Props {
  theme: any;
}

export const Breadcrumb = withTheme(({ theme }: Props) => {
  const matchesSm = useMediaQuery(theme.breakpoints.down('sm'));
  const { path: currentPath } = useCurrentRoute(breadcrumbPaths);

  if (matchesSm) return null;

  return (
    <StyledBreadcrumbContainer row centered>
      {breadcrumbPaths.map(({ path, name }: IRoutePath, index, arr) => {
        const isActive = currentPath === path;
        const isFinished = !!arr
          .slice(index + 1)
          .find((item) => item.path === currentPath);

        return (
          <Fragment key={path}>
            {!!index && <StyledBreadcrumbLine isBeforeReady={isFinished} />}
            <StyledBreadcrumbText
              className={classNames({
                'step-before': isFinished,
                'current-step': isActive,
                'step-name': !isFinished && !isActive,
              })}
              isActive={isActive}
              isFinished={isFinished}
            >
              {name}
            </StyledBreadcrumbText>
          </Fragment>
        );
      })}
    </StyledBreadcrumbContainer>
  );
});
